import { isEquipmentBased } from "common";
import { findIndex, isNumber } from "lodash";
import {
    AdjustmentOperation,
    AdjustmentType,
    AggregatorObject,
    AggregatorOperator,
    AllowedPrivacy,
    DemographicPrice,
    DemographicPricingGroup,
    Experience,
    InProductScheduleFilter,
    LineItemTemplate,
    LineItemTemplateCode,
    LineItemType,
    PricingGroup,
    PricingStructure,
    PricingTerm,
    PricingTermType,
    PricingType,
    PurchaseRule,
    PurchaseRuleAction,
    PurchaseRuleActionObject,
    PurchaseRuleFilterObject,
    PurchaseRuleFilterOperator,
    PurchaseRuleModifier,
    PurchaseRuleModifierOperation,
} from "types";
import { calculatePriceWithAdjustment } from "./DemographicPrice";
import { updateQuantityInPricingTerms } from "./DemographicPricingTerm";
import { getLineItemTemplateByCode } from "./LineItemTemplate";
import {
    getPurchaseRulesByTag,
    getReduceAggregator,
    getReturnFieldAggregator,
    getReturnValueAggregator,
    getTagIdsFromPurchaseRule,
} from "./PurchaseRule";

/**
 * Returns if the purchase rule is a base pricing purchase rule based on tags and meta
 */
export const isBasePricingPurchaseRule = (purchaseRule: PurchaseRule): boolean => {
    const tags = getTagIdsFromPurchaseRule(purchaseRule);
    if (tags.includes(PricingStructure.FLAT)) {
        return true;
    }

    if (tags.includes(PricingStructure.TIERED)) {
        return purchaseRule.meta?.tieredPricing?.min === 1 ?? false;
    }

    return false;
};

/**
 * Returns if the purchase rule is a buyout pricing purchase rule based on tags
 */
export const isBuyoutPricingPurchaseRule = (purchaseRule: PurchaseRule): boolean => {
    const tags = getTagIdsFromPurchaseRule(purchaseRule);
    if (tags.includes(PricingStructure.BUYOUT)) {
        return true;
    }

    return false;
};

/**
 * Returns the default Pricing term
 */
export const getDefaultPricingTerm = (): PricingTerm => {
    return {
        demographicPrices: [],
        type: PricingTermType.ADJUSTMENT,
    };
};

/**
 *  Generates PricingGroup Model from all purchaseRules for a particular privacy
 */
export const generatePricingGroupFromPurchaseRules = (
    purchaseRules?: PurchaseRule[],
    privacy: AllowedPrivacy = AllowedPrivacy.PUBLIC,
    retainActionId = true,
): PricingGroup => {
    const defaultPricingTerm = {
        demographicPrices: [],
        quantityMin: 1,
        type: PricingTermType.ADJUSTMENT,
    };
    const filteredPurchaseRules = getPurchaseRulesByTag(privacy, purchaseRules);
    const purchaseRuleTags =
        filteredPurchaseRules.length > 0 ? getTagIdsFromPurchaseRule(filteredPurchaseRules[0]) : [];
    const pricingStructure = getPricingStructureFromPurchaseRuleTags(purchaseRuleTags);

    const pricingGroup: PricingGroup = {
        privacy: privacy,
        pricingType: purchaseRuleTags?.includes(PricingType.PER_OUTING)
            ? PricingType.PER_OUTING
            : PricingType.PER_PERSON,
        ...(pricingStructure && { pricingStructure }),
        terms: [],
    };
    const pricingTerms: PricingTerm[] = [];
    if (filteredPurchaseRules) {
        for (const filteredPurchaseRule of filteredPurchaseRules) {
            const pricingTerm: PricingTerm = { ...defaultPricingTerm, demographicPrices: [] };
            for (const action of filteredPurchaseRule.actions) {
                const modifier = action.modifiers ? action.modifiers[0] : null;
                const accpetableLineItemActions = [
                    PurchaseRuleActionObject.INSERT_LINE_ITEM_ACTION,
                    PurchaseRuleActionObject.UPDATE_LINE_ITEM_ACTION,
                ];

                if (modifier && action.template?.id && accpetableLineItemActions.includes(action.object)) {
                    let demographicPrice: DemographicPrice = {
                        template: action.template,
                    };

                    if (retainActionId) {
                        demographicPrice.actionId = action.id;
                    }

                    // Base PurchaseRule will set value to price
                    // Non Base PurchaseRule will set value to adjustmentValue
                    if (isBasePricingPurchaseRule(filteredPurchaseRule)) {
                        demographicPrice.price = modifier.aggregator.operand as number;
                        pricingTerm.type = PricingTermType.BASE;
                    } else if (modifier.operation === PurchaseRuleModifierOperation.SET) {
                        // This is not base purchase rule, so this has adjustments

                        // First operation in buyout pricing is always set
                        if (isBuyoutPricingPurchaseRule(filteredPurchaseRule)) {
                            demographicPrice = generatedPricingTermsForBuyoutPricing(demographicPrice, modifier);
                        } else {
                            demographicPrice.adjustmentOperation = AdjustmentOperation.FINAL;
                            demographicPrice.adjustmentAmount = (modifier.aggregator?.operand as number) ?? 0;
                            demographicPrice.adjustmentType = AdjustmentType.ABSOLUTE;
                        }
                    } else {
                        // This will be an increment/decrement adjustment case
                        const isPercentAggregator = modifier.aggregator.object === AggregatorObject.PERCENT_AGGREGATOR;

                        const adjustmentAmount = isPercentAggregator
                            ? modifier.aggregator.percentValue ?? 0
                            : (modifier.aggregator.operand as number) ?? 0;

                        demographicPrice.adjustmentOperation =
                            adjustmentAmount < 0 ? AdjustmentOperation.MARKDOWN : AdjustmentOperation.MARKUP;
                        demographicPrice.adjustmentAmount = Math.abs(adjustmentAmount);
                        demographicPrice.adjustmentType = isPercentAggregator
                            ? AdjustmentType.PERCENT
                            : AdjustmentType.ABSOLUTE;
                    }

                    pricingTerm.demographicPrices?.push(demographicPrice);
                }
            }

            pricingTerm.quantityMin = filteredPurchaseRule.meta?.tieredPricing?.min ?? 1;
            pricingTerm.quantityMax = filteredPurchaseRule.meta?.tieredPricing?.max;
            pricingTerms.push(pricingTerm);
        }

        pricingGroup.terms = updateQuantityInPricingTerms(pricingTerms);
    }

    return pricingGroup;
};

const generatedPricingTermsForBuyoutPricing = (demographicPrice: DemographicPrice, modifier: PurchaseRuleModifier) => {
    return {
        ...demographicPrice,
        ...getDemographicPriceAdjustmentForBuyoutPricing(modifier),
    };
};

/**
 *  This function will generate Action items for a pricing Term.
 *  This is used by both base model and demographic pricing Group as it used only common properties while generation
 */
export const generatePurchaseRuleActionItems = (
    lineItemTemplates: LineItemTemplate[],
    pricingGroup: PricingGroup | DemographicPricingGroup,
    pricingTerm: PricingTerm,
    purchaseRule?: PurchaseRule,
    isEquipment?: boolean,
): PurchaseRuleAction[] => {
    // Removing existing Actions by passing _remove:true to API.
    const purchaseRuleActionItems: PurchaseRuleAction[] = purchaseRule?.actions
        ? purchaseRule.actions
              .filter((action) => action.id)
              .map((action) => {
                  action._remove = true;
                  return action;
              })
        : [];

    if (isBuyoutPricing(pricingGroup)) {
        return generatePurchaseRuleActionItemsForBuyoutPricingStructure(
            purchaseRuleActionItems,
            lineItemTemplates,
            pricingGroup,
        );
    }

    const hasExistingActions = purchaseRuleActionItems.length > 0;
    const fixedPriceLineItemTemplate = getLineItemTemplateByCode(
        lineItemTemplates,
        LineItemTemplateCode.PER_OUTING_PRICE,
    );

    // For Per Outing, we need to set amount of each purchasable unit line item to 0
    // But we should not set for Per_outing_Price.
    if (pricingGroup.pricingType === PricingType.PER_OUTING) {
        purchaseRuleActionItems.push({
            object: PurchaseRuleActionObject.UPDATE_LINE_ITEM_ACTION,
            filter: {
                object: PurchaseRuleFilterObject.AND_FILTER,
                operator: PurchaseRuleFilterOperator.AND,
                filters: [
                    {
                        field: "type",
                        operand: isEquipment ? LineItemType.EQUIPMENT : LineItemType.DEMOGRAPHIC,
                        object: PurchaseRuleFilterObject.EQUALS_FILTER,
                        operator: PurchaseRuleFilterOperator.EQUALS,
                    },
                    {
                        field: "templateCode",
                        operand: LineItemTemplateCode.PER_OUTING_PRICE,
                        object: PurchaseRuleFilterObject.NOT_EQUALS_FILTER,
                        operator: PurchaseRuleFilterOperator.NOT_EQUALS,
                    },
                ],
            },
            modifiers: [
                {
                    field: "amount",
                    operation: PurchaseRuleModifierOperation.SET,
                    aggregator: getReturnValueAggregator(0),
                },
            ],
        });
    }

    // One Action per one demographic price
    for (const demographicPrice of pricingTerm.demographicPrices) {
        const purchaseRuleActionItem: PurchaseRuleAction = {
            ...(demographicPrice.actionId && purchaseRule?.id && { id: demographicPrice.actionId }),
            template: { id: demographicPrice.template.id },
            object: PurchaseRuleActionObject.UPDATE_LINE_ITEM_ACTION,
            filter: {
                field: "template.id",
                operand: demographicPrice.template.id,
                object: PurchaseRuleFilterObject.EQUALS_FILTER,
                operator: PurchaseRuleFilterOperator.EQUALS,
            },
        };

        // We need to insert Fixed price line item in case of Per outing
        if (
            pricingGroup.pricingType === PricingType.PER_OUTING &&
            demographicPrice.template.id === fixedPriceLineItemTemplate?.id
        ) {
            // Will updates the existing fixed price lineitem with adjustment
            if (isNumber(demographicPrice.adjustmentAmount)) {
                purchaseRuleActionItem.modifiers = [generateModifierForAdjustment(demographicPrice, "amount")];
            } else {
                // will create a new line item since its a base price for Fixedprice lineItem
                purchaseRuleActionItem.object = PurchaseRuleActionObject.INSERT_LINE_ITEM_ACTION;
                purchaseRuleActionItem.modifiers = [
                    {
                        field: "amount",
                        operation: PurchaseRuleModifierOperation.SET,
                        aggregator: getReturnValueAggregator(demographicPrice?.price ?? 0),
                    },
                ];
            }
        } else if (
            isNumber(demographicPrice.adjustmentAmount) &&
            pricingGroup.pricingStructure !== PricingStructure.BUYOUT
        ) {
            // This will be used by both Relative Pricing & Tiered Pricing (from tier 2)
            purchaseRuleActionItem.modifiers = [generateModifierForAdjustment(demographicPrice, "price")];
        } else {
            purchaseRuleActionItem.modifiers = [
                {
                    field: "price",
                    operation: PurchaseRuleModifierOperation.SET,
                    aggregator: getReturnValueAggregator(
                        pricingGroup.pricingType === PricingType.PER_PERSON ? demographicPrice?.price ?? 0 : 0,
                    ),
                },
            ];
        }

        const index = purchaseRuleActionItem.id
            ? findIndex(purchaseRuleActionItems, { id: purchaseRuleActionItem.id })
            : -1;

        if (index >= 0) {
            purchaseRuleActionItems[index] = purchaseRuleActionItem;
        } else {
            // If PurchaseRule has Existing Actions and we are inserting an action with id
            // means the action is of different purchase rule. so we will remove the id
            if (hasExistingActions && purchaseRuleActionItem.id) {
                purchaseRuleActionItem.id = undefined;
            }

            purchaseRuleActionItems.push(purchaseRuleActionItem);
        }
    }

    return purchaseRuleActionItems;
};

/**
 * Generates Purchase Rule Action Items for Buyout Pricing Structure.
 * This puchase rule action will insert a private upgrade line item.
 *
 * To calculate the price, we will first use getMaxDemographicPricePerPerson method defined in PurchaseItem model in Xola API.
 * This method will return the max price per person from the selected demographics.
 *
 * Then we will calculate the price based on adjustment operation: "markdown" or "markup".
 * We have 2 adjustment types "percent" and "absolute":
 *
 * For percent, we will calculate the priceAdjustment and the apply multiply_aggregator:
 * eg: if we need have 10% markdown, then we will calculate 10% of 1 and subtract it from 1 to get the priceAdjustment.
 *
 * For absolute, we will calculate the priceAdjustment and apply sum_aggregator:
 * eg: if we need have 10 markdown, then the priceAdjustment will be -10
 *
 * To calculate the quantity, we need to calculate remaining spots in the event.
 * We will use difference_aggregator to subtract quantity from the outingMax.
 *
 */
export const generatePurchaseRuleActionItemsForBuyoutPricingStructure = (
    purchaseRuleActionItems: PurchaseRuleAction[],
    lineItemTemplates: LineItemTemplate[],
    pricingGroup: PricingGroup | DemographicPricingGroup,
): PurchaseRuleAction[] => {
    // Removing existing Actions by passing _remove:true to API.
    const buyoutPurchaseRuleActionItems = purchaseRuleActionItems;
    const privateUpgradeTemplate = getLineItemTemplateByCode(lineItemTemplates, LineItemTemplateCode.PRIVATE_UPGRADE);
    if (privateUpgradeTemplate?.id) {
        const demographicPrice = pricingGroup.terms[0].demographicPrices[0];
        let priceAdjustment;
        if (demographicPrice.adjustmentType === AdjustmentType.PERCENT) {
            const percentValue = (demographicPrice?.adjustmentAmount ?? 0) / 100;
            priceAdjustment =
                demographicPrice?.adjustmentOperation === AdjustmentOperation.MARKDOWN
                    ? 1 - percentValue
                    : 1 + percentValue;
        } else {
            priceAdjustment = demographicPrice?.adjustmentAmount ?? 0;
            if (demographicPrice.adjustmentOperation === AdjustmentOperation.MARKDOWN) {
                priceAdjustment = -priceAdjustment;
            }
        }

        buyoutPurchaseRuleActionItems.push({
            object: PurchaseRuleActionObject.INSERT_LINE_ITEM_ACTION,
            template: { id: privateUpgradeTemplate?.id },
            modifiers: [
                {
                    field: "price",
                    operation: PurchaseRuleModifierOperation.SET,
                    aggregator: {
                        object:
                            demographicPrice.adjustmentType === AdjustmentType.PERCENT
                                ? AggregatorObject.MULTIPLY_AGGREGATOR
                                : AggregatorObject.SUM_AGGREGATOR,
                        operands: [
                            getReturnValueAggregator(priceAdjustment),
                            getReturnFieldAggregator("maxDemographicPricePerPerson"),
                        ],
                    },
                },
                {
                    field: "quantity",
                    operation: PurchaseRuleModifierOperation.SET,
                    aggregator: {
                        object: AggregatorObject.DIFFERENCE_AGGREGATOR,
                        operands: [
                            getReturnFieldAggregator("product.group.outingMax"),
                            getReturnFieldAggregator("quantity"),
                        ],
                    },
                },
            ],
        });
    }

    return buyoutPurchaseRuleActionItems;
};

/**
 * This function is used by generatePurchaseRuleActionsItems for creating the modifier
 */
const generateModifierForAdjustment = (demographicPrice: DemographicPrice, field: string): PurchaseRuleModifier => {
    const adjustmentAmount = demographicPrice.adjustmentAmount!;
    if (demographicPrice.adjustmentOperation === AdjustmentOperation.FINAL) {
        return {
            field,
            operation: PurchaseRuleModifierOperation.SET,
            aggregator: getReturnValueAggregator(adjustmentAmount),
        };
    }

    const isMarkDown = demographicPrice.adjustmentOperation === AdjustmentOperation.MARKDOWN;

    const modifier: any = {
        field,
        operation: PurchaseRuleModifierOperation.INC,
    };

    if (demographicPrice.adjustmentType === AdjustmentType.ABSOLUTE) {
        modifier.aggregator = getReturnValueAggregator((isMarkDown ? -1 : 1) * adjustmentAmount);
    } else {
        modifier.aggregator = {
            object: AggregatorObject.PERCENT_AGGREGATOR,
            percentValue: (isMarkDown ? -1 : 1) * adjustmentAmount,
            operand: getReduceAggregator(demographicPrice.template.id, "lineItems", "template.id", field),
        };
    }

    return modifier;
};

const getInProductSchedulefilter = (purchaseRule: PurchaseRule) => {
    if (purchaseRule.filter?.object === PurchaseRuleFilterObject.IN_PRODUCT_SCHEDULE_FILTER) {
        return purchaseRule.filter;
    }

    if (purchaseRule.filter?.object === PurchaseRuleFilterObject.AND_FILTER) {
        return purchaseRule.filter.filters.find(
            (filter) => filter.object === PurchaseRuleFilterObject.IN_PRODUCT_SCHEDULE_FILTER,
        ) as InProductScheduleFilter;
    }
};

export const generatePriceVariationPurchaseRuleFromPricingGroup = (
    purchaseRuleName: string,
    experience: Experience,
    pricingGroup: PricingGroup,
    lineItemTemplates: LineItemTemplate[],
    scheduleId: ID,
    existingPurchaseRule?: PurchaseRule,
) => {
    const purchaseRule: any = existingPurchaseRule ? { ...existingPurchaseRule } : {};
    purchaseRule.name = `${purchaseRuleName} - ${pricingGroup.privacy}`;
    const inProductScheduleFilter = getInProductSchedulefilter(purchaseRule);

    purchaseRule.filter = {
        object: PurchaseRuleFilterObject.AND_FILTER,
        operator: PurchaseRuleFilterOperator.AND,
        filters: [
            {
                object: PurchaseRuleFilterObject.IN_PRODUCT_SCHEDULE_FILTER,
                operator: PurchaseRuleFilterOperator.IN_PRODUCT_SCHEDULE,
                product: { id: experience.id },
                schedules: {
                    all: false,
                    items: inProductScheduleFilter?.schedules?.items
                        ? [...inProductScheduleFilter.schedules.items.filter((item) => item !== scheduleId), scheduleId]
                        : [scheduleId],
                },
            },
            {
                field: "privacy",
                object: PurchaseRuleFilterObject.EQUALS_FILTER,
                operator: PurchaseRuleFilterOperator.EQUALS,
                operand: pricingGroup.privacy,
            },
        ],
    };
    const pricingTerm = {
        ...pricingGroup.terms[0],
        demographicPrices: pricingGroup.terms?.[0]?.demographicPrices
            ? pricingGroup.terms[0].demographicPrices.filter(
                  (demographicPrice) =>
                      isNumber(demographicPrice.adjustmentAmount) && demographicPrice.adjustmentAmount >= 0,
              )
            : [],
    };
    purchaseRule.actions = generatePurchaseRuleActionItems(
        lineItemTemplates,
        pricingGroup,
        pricingTerm,
        existingPurchaseRule,
        isEquipmentBased(experience),
    );
    purchaseRule.seller = { id: experience.seller.id };
    purchaseRule.tags = [{ id: "schedule" }, { id: pricingGroup.privacy }, { id: pricingGroup.pricingType }];
    purchaseRule.meta = {
        ...(purchaseRule.meta ? purchaseRule.meta : {}),
        product: experience.id,
        schedules: purchaseRule.meta?.schedules
            ? [...purchaseRule.meta.schedules.filter((id: ID) => id !== scheduleId), scheduleId]
            : [scheduleId],
        variationWeight: getVariationWeight(pricingTerm),
    };

    return purchaseRule as PurchaseRule;
};

export const getAdjustmentValuesFromPurchaseRule = (purchaseRule: PurchaseRule) => {
    if (purchaseRule) {
        const adjustmentValues = [];
        for (const action of purchaseRule.actions) {
            const { modifiers, template } = action;
            if (template?.id) {
                const adjustmentValue = {
                    templateId: template?.id,
                    adjustmentAmount:
                        modifiers?.[0].aggregator?.object === AggregatorObject.PERCENT_AGGREGATOR
                            ? modifiers?.[0].aggregator.percentValue
                            : modifiers?.[0].aggregator.operand ?? 0,
                    adjustmentType: modifiers?.[0].operation,
                    adjustmentOperation:
                        modifiers?.[0].aggregator?.object === AggregatorObject.PERCENT_AGGREGATOR
                            ? "percent"
                            : "absolute",
                };
                adjustmentValues.push(adjustmentValue);
            }
        }

        return adjustmentValues;
    }

    return [];
};

const getVariationWeight = (pricingTerm: PricingTerm) => {
    /**
     * In order to Mimic V1 closely, We are using the concept of variation weight.
     * We are calculating the difference between the aggregate base price and aggregate effective price to get the maximum variation.
     * Schedule purchase rules are ordered(asc) based on the variation weight. The last applicable schedule rule (max variation) is picked in case of overlapping schedules.
     **/
    let aggregatedEffectivePrice = 0;
    let aggregatedBasePrice = 0;
    for (const demographicPrice of pricingTerm.demographicPrices) {
        aggregatedEffectivePrice += calculatePriceWithAdjustment(demographicPrice.price ?? 0, demographicPrice) ?? 0;
        aggregatedBasePrice += demographicPrice.price ?? 0;
    }

    return (aggregatedEffectivePrice - aggregatedBasePrice) / pricingTerm.demographicPrices.length;
};

export const getPricingStructureFromPurchaseRuleTags = (tags: string[]) => {
    if (tags.includes(PricingStructure.TIERED)) {
        return PricingStructure.TIERED;
    }

    if (tags.includes(PricingStructure.RELATIVE)) {
        return PricingStructure.RELATIVE;
    }

    if (tags.includes(PricingStructure.BUYOUT)) {
        return PricingStructure.BUYOUT;
    }

    return PricingStructure.FLAT;
};

const getDemographicPriceAdjustmentForBuyoutPricing = (modifier: PurchaseRuleModifier) => {
    const demographicPrice: any = {};
    demographicPrice.adjustmentType =
        modifier.aggregator.operator === AggregatorOperator.MULTIPLY ? AdjustmentType.PERCENT : AdjustmentType.ABSOLUTE;

    demographicPrice.adjustmentAmount = 0;
    demographicPrice.adjustmentOperation = AdjustmentOperation.MARKUP;
    if (modifier.aggregator.operands && modifier.aggregator.operands.length > 0) {
        const operand = (modifier.aggregator.operands[0].operand as number) ?? 0;
        if (demographicPrice.adjustmentType === AdjustmentType.PERCENT) {
            if (operand < 1) {
                demographicPrice.adjustmentAmount = 100 - operand * 100;
                demographicPrice.adjustmentOperation = AdjustmentOperation.MARKDOWN;
            } else {
                demographicPrice.adjustmentAmount = operand * 100 - 100;
                demographicPrice.adjustmentOperation = AdjustmentOperation.MARKUP;
            }
        } else if (operand < 0) {
            demographicPrice.adjustmentAmount = -operand;
            demographicPrice.adjustmentOperation = AdjustmentOperation.MARKDOWN;
        } else {
            demographicPrice.adjustmentAmount = operand;
            demographicPrice.adjustmentOperation = AdjustmentOperation.MARKUP;
        }
    }

    return demographicPrice;
};

export const isBuyoutPricing = (pricingGroup: PricingGroup | DemographicPricingGroup) => {
    return pricingGroup?.pricingStructure === PricingStructure.BUYOUT;
};
